import { type QueryKey } from '@tanstack/react-query';
import {
	type FileRequest,
	type ShipmentRequirementDTO,
	type ShipmentUnitDTO,
	type ShippingLineDTO,
	UturnFileDTO,
} from '@uturn/api/v1';
import {
	type Carrier,
	type ShipmentDtoShipmentStatus,
	type ShipmentDtoTsoStatus,
} from '@uturn/api/v3';
import type { CarrierReview, Document } from '@uturn/portal/types/shipment';
import { Card, CardContent, CardHeader, CardTitle } from '@uturn/ui-kit';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AdditionalInformation } from './additional-information';
import { CargoDetails } from './cargo-details';
import { CarrierInformation } from './carrier-information';
import { ContainerDetails } from './container-details';
import { ShipmentGroup } from './shipment-group';
import { ShipmentStatusInfo } from './shipment-status-info';
import { ShippingRequirements } from './shipping-requirements';

// TODO: once api has been refactored, we have to provide the correct types instead of any.
type ShipmentDetailsSectionProps = {
	containerTypes: ShipmentUnitDTO[];
	shippingLines: ShippingLineDTO[];
	shipmentRequirements: ShipmentRequirementDTO[];
	setFileRequests?: (fileRequests: FileRequest[]) => void;
	setFiles?: (files: File[]) => void;
	shipmentNumber?: number;
	carrier?: Carrier;
	carrierReview?: CarrierReview;
	shipmentStatus?: ShipmentDtoShipmentStatus;
	tsoStatus?: ShipmentDtoTsoStatus;
	initDocuments?: UturnFileDTO[];
	setNewFiles?: (files: File[], fileRequests: FileRequest[]) => void;
	uploadDocument?: (
		file: File,
		fileRequest: Partial<FileRequest>,
	) => Promise<void>;
	documentQueryKey?: QueryKey;
	enableAddingGroup?: boolean;
};

export function ShipmentDetailsSection({
	containerTypes,
	shippingLines,
	shipmentRequirements,
	shipmentNumber,
	carrier,
	carrierReview,
	shipmentStatus,
	tsoStatus,
	initDocuments = [],
	setNewFiles,
	uploadDocument,
	documentQueryKey,
	enableAddingGroup,
}: ShipmentDetailsSectionProps) {
	const { t } = useTranslation();
	const [documents, setDocuments] = useState<Document[]>([]);

	useEffect(() => {
		if (!setNewFiles) {
			return;
		}
		const fileRequests: FileRequest[] = [];
		const files: File[] = [];

		documents.forEach((document) => {
			fileRequests.push(document.fileRequest as FileRequest);
			files.push(document.file);
		});

		setNewFiles(files, fileRequests);
	}, [documents]);

	return (
		<div className="relative h-full">
			<Card>
				<CardHeader>
					<CardTitle className="text-lg">
						{t(
							'pages.create_shipment.shipment-details.heading',
							'Shipment information',
						)}
					</CardTitle>
				</CardHeader>
				<CardContent className="flex flex-col gap-7">
					<div
						className={`flex flex-col gap-4 ${
							!shipmentStatus && !carrier && !enableAddingGroup ? 'hidden' : ''
						}`}
					>
						{enableAddingGroup && <ShipmentGroup />}
						{(shipmentStatus || tsoStatus) && (
							<ShipmentStatusInfo
								shipmentStatus={shipmentStatus}
								orderStatus={tsoStatus}
							/>
						)}

						{carrier && (
							<CarrierInformation
								shipmentNumber={shipmentNumber}
								carrier={carrier}
								carrierReview={carrierReview}
							/>
						)}
					</div>

					<ContainerDetails
						shipmentNumber={shipmentNumber}
						containerTypesRaw={containerTypes}
						shippingLinesRaw={shippingLines}
						enableAddingGroup={enableAddingGroup ?? false}
					/>

					<CargoDetails />

					<ShippingRequirements requirements={shipmentRequirements} />

					<AdditionalInformation
						initDocuments={initDocuments}
						setDocuments={setDocuments}
						uploadDocument={uploadDocument}
						documentQueryKey={documentQueryKey}
					/>
				</CardContent>
			</Card>
		</div>
	);
}
